"use client";

import { useState, useCallback, useEffect } from "react";
import type { DialogNode } from "@/lib/dialog-data";
import { Loader2, Send, X, Mic, Square } from "lucide-react";
import { useLanguage } from "@/lib/language-context";
import { useTranslations } from "@/lib/use-translations";
import { useSpeechToText } from "@/lib/use-speech-to-text";
import api from "@/lib/api/client";

function RecordingTimer({ startTime }: { startTime: number | null }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startTime) {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 100);
    return () => clearInterval(interval);
  }, [startTime]);

  const seconds = Math.floor(elapsed / 1000);
  const minutes = Math.floor(seconds / 60);
  const displaySeconds = seconds % 60;

  return (
    <div className="text-lg font-mono text-destructive font-medium">
      {minutes}:{displaySeconds.toString().padStart(2, "0")}
    </div>
  );
}

interface FixResponseModalProps {
  node: DialogNode;
  speaker?: string;
  topicTitle?: string;
  onClose: () => void;
  onNodeFixed: (updatedNode: DialogNode, userInput: string) => void;
}

export function FixResponseModal({
  node,
  speaker,
  topicTitle,
  onClose,
  onNodeFixed,
}: FixResponseModalProps) {
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { nativeLanguage, targetLanguage } = useLanguage();
  const t = useTranslations();

  const sttHook = useSpeechToText({
    language: nativeLanguage.code || "en-US",
    onResult: (text) => {
      setInput(prev => prev ? `${prev} ${text}` : text);
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { data, response } = await api.POST("/api/refine-phrase", {
        body: {
          mode: "fix",
          userInput: input,
          originalText: node.text,
          originalTranslation: node.translation,
          nativeLanguage: nativeLanguage.code,
          targetLanguage: targetLanguage.code,
          context: {
            speaker: speaker || "Person",
            scenario: topicTitle || "conversation",
          },
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fix response");
      }

      const result = data as Record<string, Record<string, string>>;
      const fixedPhrase = result.fixedPhrase;
      if (!fixedPhrase?.text || !fixedPhrase.translation) {
        throw new Error("Invalid response from fix API");
      }

      const updatedNode: DialogNode = {
        ...node,
        text: fixedPhrase.text,
        translation: fixedPhrase.translation,
      };

      onNodeFixed(updatedNode, input);
      onClose();
    } catch (error) {
      console.error("Fix response error:", error);
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  }, [input, isSubmitting, node, nativeLanguage.code, targetLanguage.code, speaker, topicTitle, onNodeFixed, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      <div className="relative w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-foreground">
            {t.learning.fixThisResponse || "Fix this response"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <p className="text-xs text-muted-foreground mb-1">
            {t.learning.said || "Current"}:
          </p>
          <p className="text-sm text-foreground">{node.text}</p>
          <p className="text-xs text-muted-foreground italic mt-1">
            {node.translation}
          </p>
        </div>

        <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
          <label className="block text-sm font-medium text-foreground text-center">
            {t.learning.whatsWrong || "What should it say instead?"}
          </label>

          {errorMessage && (
            <p className="text-xs text-destructive text-center">{errorMessage}</p>
          )}

          {sttHook.error && !sttHook.isProcessing && !sttHook.isRecording && (
            <p className="text-xs text-destructive text-center">{sttHook.error}</p>
          )}

          {sttHook.isProcessing ? (
            <div className="flex gap-2 items-center">
              <div className="flex-1 px-3 py-2.5 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.learning.transcribing}
              </div>
            </div>
          ) : sttHook.isRecording ? (
            <div className="flex gap-2 items-center">
              <span className="flex-1 text-sm text-muted-foreground">{t.dialog.listening}</span>
              <RecordingTimer startTime={sttHook.recordingStartTime} />
              <button
                type="button"
                onClick={sttHook.stopRecording}
                className="p-2.5 rounded-lg bg-destructive text-destructive-foreground animate-pulse"
                title={t.dialog.stopRecording}
              >
                <Square className="w-5 h-5 fill-current" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder=""
                className="flex-1 px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                disabled={isSubmitting}
                autoFocus
              />
              <button
                type="button"
                onClick={sttHook.startRecording}
                disabled={isSubmitting}
                className="p-2.5 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground hover:text-primary transition-colors"
                title={t.learning.useVoice}
              >
                <Mic className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!input.trim() || isSubmitting}
                className="p-2.5 bg-primary text-primary-foreground rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title={t.common.send}
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
